/**
 * @file BaseComponents.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import React from "react";
import { StorageKey } from "../../login/utils/StorageKey";
import { container } from "../../../di/inversify.config";
import { TYPES } from "../../../logger/Types";
import { Logger } from "../../../logger/Logger";
var CryptoJS = require("crypto-js");  // eslint-disable-line

 /**
 * A class definition for BaseComponent
 */
export class BaseComponent<BaseProps, State> extends React.Component<BaseProps, State> {

    protected logger: Logger = container.get<Logger>(TYPES.LoggerService);
    private secretKey = process.env.REACT_APP_SECRET_KEY;
    constructor(props: BaseProps) {
        super(props);
        // this.secretKey = TODO add key from env
    }

    /**
     * Verify is user LoggedIn
     * @returns boolean
     */
    protected isLoggedIn(): boolean {
        const isLoggedIn = this.getIsLoggedIn();

        if (this.isEmpty(isLoggedIn)) {
            return false;
        }

        const sessionToken = this.getSesionToken();

        if (this.isEmpty(sessionToken)) {
            return false;
        }

        if (!Object.keys(sessionToken).includes('accessToken')) {
            return false;
        }

        const clinic = this.getSelectedClinic();
        if(this.isEmpty(clinic.trim())){
            return false;
        }
        if (!Object.keys(JSON.parse(clinic)).includes('id')) {
            return false;
        }

        return true;
    }

    protected useQuery = () => new URLSearchParams(window.location?.search);

    protected getAuthToken = (query: URLSearchParams) => query.get('authToken');
    
    protected getUserName = (query: URLSearchParams) => query.get('name');


    /**
     * Logout existing log in users
     * @returns boolean
     */
    protected logOut(): boolean {
        this.setSesionToken('');
        this.setSelectedClinic('');
        this.setLoggedInUserRole('');
        this.setAllClinics('');
        this.setIsLoggedIn(false);
        this.setUserDetails('');

        return true;
    }

    /**
     * Validates and get session
     */
    protected validateAndGetSession (name: string) : any | undefined {
        const sessionToken = this.getSesionToken();
        if(this.isEmpty(sessionToken)) {
            this.logger.warning("Empty access token", name);
            return undefined;
        }
        if(!Object.keys(sessionToken).includes("accessToken") || sessionToken.accessToken.length <= 0) {
            this.logger.warning("loadPatients()- Empty access token", name);
            return undefined;
        }

        return sessionToken.accessToken;
    }

    /**
     * Gets Access token
     * @returns AccessToken
     */
    // eslint-disable-next-line
    protected getSesionToken = () => !this.isEmpty(this.getValueFromSessionStorage(StorageKey.APP_SESSION_KEY)) ? JSON.parse(this.getValueFromSessionStorage(StorageKey.APP_SESSION_KEY)!!) : '';

    /**
     * Gets selected clinic
     * @returns 
     */
    /* eslint-disable @typescript-eslint/no-extra-non-null-assertion*/
    protected getSelectedClinic = () => this.getValueFromSessionStorage(StorageKey.CLINIC_KEY)!!;

    /**
     * Gets list of all clinics
     * @returns 
     */
    protected getAllClinics = () => this.getValueFromSessionStorage(StorageKey.CLINIC_LIST_KEY)!!;

    /**
     * Gets current user details
     * @returns 
     */
    protected getCurrentUser = () => this.getValueFromSessionStorage(StorageKey.CURRENT_USER)!!;


    /**
     * A utility for checking if given string is empty or not
     * @param str string
     * @returns 
     */
    protected isEmpty(str) {
        return (!str || str.length === 0);
    }

    protected isJson(str) {
         if(!str || str.length === 0)
                    return false;
                    try {
                        JSON.parse(str);
                    } catch (e) {
                        return false;
                    }   
        return true;     
    }

    /**
     * Gets current loggedIn status
     * @returns boolean
     */
    protected getIsLoggedIn = () => this.getValueFromSessionStorage(StorageKey.IsLoggedIn) && this.getValueFromSessionStorage(StorageKey.IsLoggedIn) == 'true' ? true : false;

    /**
     * Sets current loggedIn status
     * @param isLoggedIn boolean
     * @returns 
     */
    protected setIsLoggedIn = (isLoggedIn: boolean) => this.setValueFromSessionStorage(StorageKey.IsLoggedIn, isLoggedIn);

    /**
     * Sets access token
     * @param jwtToken jwtToken
     * @returns 
     */
    protected setSesionToken = (jwtToken) => this.setValueFromSessionStorage(StorageKey.APP_SESSION_KEY, jwtToken);

    /**
     * Sets user details
     * @param userDetails 
     * @returns 
     */
    protected setUserDetails = (userDetails) => this.setValueFromSessionStorage(StorageKey.CURRENT_USER, userDetails);

    /**
     * Sets clinic
     * @param clinic Clinic
     * @returns 
     */
    protected setSelectedClinic = (clinic) => this.setValueFromSessionStorage(StorageKey.CLINIC_KEY, clinic);

    /**
     * Sets clinic
     * @param clinics Clinic[]
     * @returns 
     */
    protected setAllClinics = (clinics) => this.setValueFromSessionStorage(StorageKey.CLINIC_LIST_KEY, clinics);

    /**
     * Sets logged in user role
     * @param role RoleType
     * @returns 
     */
    protected setLoggedInUserRole = (role) => this.setValueFromSessionStorage(StorageKey.ROLE_TYPE, role);

    /**
     * Gets logged in user role
     * @returns RoleType
     */
    protected getLoggedInUserRole = () => this.getValueFromSessionStorage(StorageKey.ROLE_TYPE);

    /**
     * Get generic value from sessionStorage
     * @param storage StorageKey
     * @returns 
     */
    private getValueFromSessionStorage = (storage: StorageKey) => {
        const encryptedText = window.localStorage.getItem(storage);
        if(!encryptedText){
            return '';
        }
        const decryptedText = CryptoJS.AES.decrypt(encryptedText, this.secretKey).toString(CryptoJS.enc.Utf8);
        this.logger.warning(`decrypted -${storage} - ${decryptedText}`, BaseComponent.name);
        return decryptedText;
    }

    /**
     * Sets values into sessionStorage
     * @param storageKey StorageKey
     * @param value string
     * @returns 
     */
    private setValueFromSessionStorage = (storageKey: StorageKey, value: any) =>{ 
        this.logger.warning(`setValueFromSessionStorage() - Encrypting....${value}`, BaseComponent.name);
        const encryptedText = CryptoJS.AES.encrypt(JSON.stringify(value), this.secretKey).toString();
        const decryptedText = CryptoJS.AES.decrypt(encryptedText, this.secretKey).toString(CryptoJS.enc.Utf8);
        this.logger.warning(`Decrypted - ${decryptedText}`, BaseComponent.name);

        this.logger.warning(`Encrypting ... encryptedText - ${storageKey} ${encryptedText}`, BaseComponent.name);
        window.localStorage.setItem(storageKey, encryptedText);
    };

}